// components/climate/flood/FloodSummary.tsx
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { EmissionTier, MetricState, canonicalPeriodLabels, CanonicalPeriod } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { useCurrentUser } from '../../../../hooks/users'
import { FloodScoreBadge } from '../../common/flood/utils'
import { capitalise } from 'balkerne-fn'

const SCORE_METRIC_KEY = 'fld:score'
const AAL_METRIC_KEY = 'fld:average_damage_loss'
const BI_DAYS_METRIC_KEY = 'fld:average_bi_days'

const PERIOD_ORDER: CanonicalPeriod[] = ['short', 'mid', 'long'] as CanonicalPeriod[]
type PeriodCanonical = 'short' | 'mid' | 'long'

function sortPeriods(periods: string[]): string[] {
  return [...periods].sort((a, b) => PERIOD_ORDER.indexOf(a as any) - PERIOD_ORDER.indexOf(b as any))
}

function formatNumber(value: number | null | undefined, digits = 0) {
  if (value == null || Number.isNaN(value)) return '–'
  return value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })
}

function formatLoss(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '–'
  return value.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })
}

function getSucceededMetricData<T>(metric: { state?: { value?: MetricState }; data?: T | null } | undefined) {
  if (!metric || metric.state?.value !== MetricState.SUCCEEDED) return null
  return metric.data ?? null
}

function FloodSummary({ propertyId, emissionTier }: { propertyId: number; emissionTier: EmissionTier }) {
  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(SCORE_METRIC_KEY, { propertyId })

  const {
    data: aalData,
    isLoading: aalLoading,
    error: aalError,
  } = usePropertyClimateData(AAL_METRIC_KEY, { propertyId })

  const {
    data: biData,
    isLoading: biLoading,
    error: biError,
  } = usePropertyClimateData(BI_DAYS_METRIC_KEY, { propertyId })
  const { currencySymbol } = useCurrentUser()

  const scorePayload = getSucceededMetricData(scoreData)
  const aalPayload = getSucceededMetricData(aalData)
  const biPayload = getSucceededMetricData(biData)

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodCanonical>('mid')

  const loading = scoreLoading || aalLoading || biLoading
  const allErrored = scoreError && aalError && biError

  // Derive available periods from any metric that has period_canonical keys
  const availablePeriods: string[] = useMemo(() => {
    const fromScore = (scorePayload?.keys?.period_canonical as string[] | undefined) || []
    const fromAal = (aalPayload?.keys?.period_canonical as string[] | undefined) || []
    const fromBi = (biPayload?.keys?.period_canonical as string[] | undefined) || []

    let allCanonicalPeriods = Array.from(new Set([...fromScore, ...fromAal, ...fromBi]))
    if (!allCanonicalPeriods.length) {
      allCanonicalPeriods = PERIOD_ORDER as string[]
    }
    return sortPeriods(allCanonicalPeriods)
  }, [scorePayload, aalPayload, biPayload])

  const effectivePeriod: any = useMemo(() => {
    if (availablePeriods.includes(selectedPeriod)) return selectedPeriod
    if (availablePeriods.includes('mid')) return 'mid'
    return availablePeriods[0]
  }, [availablePeriods, selectedPeriod])

  // ---- Score for selected period ----
  const tierScoreData = scorePayload?.data?.[emissionTier]
  const scorePointsForPeriod = tierScoreData?.[effectivePeriod] as { x: number; y: number }[] | undefined
  const scoreValue = scorePointsForPeriod?.[0]?.y
  const scoreReady = scoreData && scoreData.state.value === MetricState.SUCCEEDED

  // ---- AAL total for selected period ----
  const tierAalData = aalPayload?.data?.[emissionTier] as
    | Record<string, Record<string, Record<string, { x: number; y: number }[]>>>
    | undefined

  let aalTotal: number | null = null
  let aalPeriodData: Record<string, Record<string, { x: number; y: number }[]>> | undefined

  if (aalData && aalData.state.value === MetricState.SUCCEEDED && tierAalData) {
    aalPeriodData = tierAalData[effectivePeriod]
    if (aalPeriodData) {
      let sum = 0
      for (const target of Object.values(aalPeriodData)) {
        for (const series of Object.values(target || {})) {
          const pts = series || []
          for (const pt of pts) {
            sum += pt.y
          }
        }
      }
      aalTotal = sum
    }
  }

  // ---- BI days by source for selected period ----
  const tierBiData = biPayload?.data?.[emissionTier] as
    | Record<string, Record<string, { x: number; y: number }[]>>
    | undefined

  const biPeriodData = tierBiData?.[effectivePeriod]
  const biSources = (biPayload?.keys?.source as string[] | undefined) || []
  const biReady = biData && biData.state.value === MetricState.SUCCEEDED && !!biPeriodData

  // ---- AAL breakdown table structure ----
  // Derive targets/sources from keys if present, otherwise from period data
  let aalTargets: string[] = (aalPayload?.keys?.target as string[] | undefined) || []
  let aalSources: string[] = (aalPayload?.keys?.source as string[] | undefined) || []

  if (aalPeriodData) {
    if (!aalTargets.length) {
      aalTargets = Object.keys(aalPeriodData)
    }
    if (!aalSources.length && aalTargets.length) {
      const firstTargetData = aalPeriodData[aalTargets[0]]
      if (firstTargetData) {
        aalSources = Object.keys(firstTargetData)
      }
    }
  }

  const getDamageLossCellValue = (target: string, source: string): number | null => {
    if (!aalPeriodData) return null
    const targetData = aalPeriodData[target]
    if (!targetData) return null
    const series = targetData[source] || []
    if (!series.length) return null
    return series.reduce((sum, pt) => sum + pt.y, 0)
  }

  const aalHasAnyData =
    !!aalPeriodData &&
    aalTargets.some(target => aalSources.some(source => getDamageLossCellValue(target, source) != null))

  if (loading && !scoreData && !aalData && !biData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Could not load flood summary data.</Alert>
  }

  return (
    <Box>
      {/* Header + period control */}
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <Box>
          <Typography variant="h6">Overview</Typography>
          <Typography variant="body2" color="text.secondary">
            Summary of flood risk for the selected period
          </Typography>
        </Box>

        {availablePeriods.length > 1 && (
          <ToggleButtonGroup
            size="small"
            value={effectivePeriod}
            exclusive
            onChange={(_, value) => {
              if (value) setSelectedPeriod(value)
            }}>
            {availablePeriods.map(period => (
              <ToggleButton key={period} value={period}>
                {canonicalPeriodLabels[period]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Summary cards row */}
      <Grid container spacing={2} mb={2}>
        {/* Score card */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Risk Rating
              </Typography>

              {scoreReady && scoreValue != null ? (
                <Box>
                  <FloodScoreBadge score={scoreValue} size="compact" />
                </Box>
              ) : scoreData && scoreData.state.value !== MetricState.SUCCEEDED ? (
                <Typography variant="body2" color="text.secondary">
                  Flood risk rating not available (state: {scoreData.state.value}).
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No risk rating data for this period.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* AAL total card */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Annual Average Loss (AAL)
              </Typography>
              {aalData && aalData.state.value !== MetricState.SUCCEEDED ? (
                <Typography variant="body2" color="text.secondary">
                  AAL data not available (state: {aalData.state.value}).
                </Typography>
              ) : aalTotal != null ? (
                <Typography variant="h4" component="div">
                  {`${currencySymbol}${formatNumber(aalTotal, 0)}`}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No AAL data for this period.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* BI days card */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Business Interruption
              </Typography>

              {biData && biData.state.value !== MetricState.SUCCEEDED ? (
                <Typography variant="body2" color="text.secondary">
                  BI days data not available (state: {biData.state.value}).
                </Typography>
              ) : biReady && biSources.length ? (
                <Box component="dl" sx={{ m: 0 }}>
                  {biSources.map(source => {
                    const series = biPeriodData?.[source] || []
                    const value = series[0]?.y
                    return (
                      <Box key={source} display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                        <Typography
                          component="dt"
                          variant="body2"
                          color="text.secondary"
                          sx={{ textTransform: 'capitalize' }}>
                          {source}
                        </Typography>
                        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                          {value != null ? `${formatNumber(value, 1)} days / year` : '–'}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No business interruption data for this period.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* AAL breakdown table */}
      <Card variant="outlined">
        <CardContent>
          <Box mb={1}>
            <Typography variant="subtitle2">AAL Breakdown</Typography>
          </Box>
          {aalData && aalData.state.value !== MetricState.SUCCEEDED ? (
            <Typography variant="body2" color="text.secondary">
              Financial loss due to damage data not available (state: {aalData.state.value}).
            </Typography>
          ) : !aalPeriodData || !aalTargets.length || !aalSources.length || !aalHasAnyData ? (
            <Typography variant="body2" color="text.secondary">
              No financial loss breakdown data for this period.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell> </TableCell>
                  {aalSources.map(source => (
                    <TableCell key={source} align="right">
                      <Typography variant="body2" color="text.secondary">
                        {capitalise(source)} flood
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {aalTargets.map(target => (
                  <TableRow key={target}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2">{capitalise(target)} damage</Typography>
                    </TableCell>
                    {aalSources.map(source => {
                      const value = getDamageLossCellValue(target, source)
                      return (
                        <TableCell key={source} align="right">
                          <Typography variant="body2">
                            {value == null || Number.isNaN(value)
                              ? '–'
                              : `${currencySymbol}${formatLoss(value)} / year`}
                          </Typography>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default FloodSummary
